import { Input, message, Select, Space, Table, Typography, Button, Modal } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { ExportOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { searchUsers } from 'services';
import { ColumnMainAccountDataType, ColumnSubAccountDataType } from 'types/table';
import { convertDateTime } from 'utils';
import { Helmet } from "react-helmet";
import StaleAccounts from './StaleAccounts';
import AuditLogs from './AuditLogs';

const { Search } = Input;

const parsingMainAccounts = (data: any) => data.map((item: any, index: number) => ({
  key: `row-main-${item.mauticContactId || `no-contact-${index}`}`,
  email: item.email,
  firstName: item.firstName,
  lastName: item.lastName,
  amount: item.amount,
  contactId: item.mauticContactId,
  plan: item.plan,
  hazType: item.userHazType,
  influencer: item.influencer && String(item.influencer),
  registerFrom: item.registerFrom,
}));

const parsingSubAccounts = (data: any) => data.map((item: any, index: number) => ({
  key: `row-sub-${item.mauticContactId || `no-contact-${index}`}`,
  email: item.email,
  firstName: item.firstName,
  lastName: item.lastName,
  isOnline: item.isOnline && String(item.isOnline),
  lastedAccess: item.lastedAccess,
  extVersion: item.extVersion,
  updatedAt: item.updatedAt,
  manager: item.manager.email,
}))

const mainAccountColumns: ColumnsType<ColumnMainAccountDataType> = [
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    render: (value: string) => (
      <Space size="large">
        <Link to={`/user/${value}`}>{value}</Link>
        <Link to={`/user/${value}`} target="_blank" rel="noopener noreferrer"><ExportOutlined /></Link>
      </Space>
    )
  },
  {
    title: 'First name',
    dataIndex: 'firstName',
    key: 'firstName',
  },
  {
    title: 'Last name',
    dataIndex: 'lastName',
    key: 'lastName',
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    render: (value: any) => value || 'None'
  },
  {
    title: 'Contact Id',
    dataIndex: 'contactId',
    key: 'contactId',
    render: (value: any) => value || 'None'
  },
  {
    title: 'Plan',
    dataIndex: 'plan',
    key: 'plan',
    render: (value: any) => value || 'None'
  },
  {
    title: 'Haz Type',
    dataIndex: 'hazType',
    key: 'hazType',
    render: (value: any) => value || 'None'
  },
  {
    title: 'Influencer',
    dataIndex: 'influencer',
    key: 'influencer',
    render: (value: any) => value || 'None'
  },
  {
    title: 'Register From',
    dataIndex: 'registerFrom',
    key: 'registerFrom',
    render: (value: any) => value || 'None'
  },
];

const subAccountColumns: ColumnsType<ColumnSubAccountDataType> = [
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'First name',
    dataIndex: 'firstName',
    key: 'firstName',
  },
  {
    title: 'Last name',
    dataIndex: 'lastName',
    key: 'lastName',
  },
  {
    title: 'Is Online',
    dataIndex: 'isOnline',
    key: 'isOnline',
    render: (value: any) => value || 'None',
  },
  {
    title: 'Lasted Access',
    dataIndex: 'lastedAccess',
    key: 'lastedAccess',
    render: (value: any) => value || 'None',
  },
  {
    title: 'Extension Version',
    dataIndex: 'extVersion',
    key: 'extVersion',
    render: (value: any) => value || 'None',
  },
  {
    title: 'Updated',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (value: any) => convertDateTime(value),
  },
  {
    title: 'Manager',
    dataIndex: 'manager',
    key: 'manager',
    render: (value: string) => (
      <Link to={`/user/${value}`} target="_blank" rel="noopener noreferrer">{value}</Link>
    )
  },
];

const UserManagement = () => {
  const [loadingTable, setLoadingTable] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<any>({
    users: [],
    members: [],
  });
  const [keyword, setKeyword] = useState<string | undefined>()
  const [showStaleAccounts, setShowStaleAccounts] = useState<boolean>(false);
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const searchKeyword = searchParams.get('search');

  useEffect(() => {
    if (searchKeyword) {
      setKeyword(searchKeyword);
      handleOnSearch(searchKeyword);
    }
  }, [searchKeyword])

  const handleOnSearch = (value: string) => {
    if (!value) {
      return;
    }

    setLoadingTable(true);
    const params = { search: decodeURI(value) };
    searchUsers({ params })
      .then((response: any) => {
        try {
          // Handle response structure: axios wraps response in data property
          // API returns: { data: { users: [], members: [] } }
          // Axios wraps it: { data: { data: { users: [], members: [] } } }
          const responseData = response?.data?.data || response?.data;
          const users = responseData?.users || [];
          const members = responseData?.members || [];

          const parsedUserData = parsingMainAccounts(users);
          const parsedMemberData = parsingSubAccounts(members);

          setDataTable({
            users: parsedUserData,
            members: parsedMemberData,
          });
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          message.error("Can not search user");
        } finally {
          setLoadingTable(false);
        }
      })
      .catch((error: any) => {
        console.error('Search error:', error);
        message.error("Can not search user");
        setLoadingTable(false);
      })
  }

  return (
    <>
      <Helmet>
        <title>Search | User management</title>
      </Helmet>
      <PageHeader
        className="page-header"
        title="User Management"
        footer={
          <Space direction="vertical" size="large" style={{ marginTop: 10, width: '100%' }}>
            <Space>
              <Search
                placeholder="Search by emails, name, contactId or sellerId"
                allowClear
                value={keyword}
                onChange={(e: any) => setKeyword(e.target.value)}
                onSearch={(value: string) => {
                  const kw = value.trim();
                  setKeyword(kw);
                  handleOnSearch(kw);
                }}
                style={{ width: 400 }}
                enterButton
                loading={loadingTable}
              />
              <Button
                type="primary"
                onClick={() => setShowStaleAccounts(true)}
              >
                Filter Stale Accounts
              </Button>
              <Button
                type="default"
                onClick={() => setShowAuditLogs(true)}
              >
                View Audit Logs
              </Button>
            </Space>

            <Typography.Title level={3}>Users</Typography.Title>
            <Table
              className="table-striped-row"
              columns={mainAccountColumns}
              dataSource={dataTable.users}
              loading={loadingTable}
            />
            <Space direction="vertical" size="large" style={{ marginTop: 50, width: '100%' }}>
              <Typography.Title level={3}>Members</Typography.Title>
              <Table
                className="table-striped-row"
                columns={subAccountColumns}
                dataSource={dataTable.members}
                loading={loadingTable}
              />
            </Space>

            <Modal
              title="Filter Stale Accounts"
              open={showStaleAccounts}
              onCancel={() => setShowStaleAccounts(false)}
              footer={null}
              width={1200}
              destroyOnClose
            >
              <StaleAccounts onClose={() => setShowStaleAccounts(false)} />
            </Modal>

            <Modal
              title="Admin Audit Logs"
              open={showAuditLogs}
              onCancel={() => setShowAuditLogs(false)}
              footer={null}
              width={1400}
              destroyOnClose
            >
              <AuditLogs onClose={() => setShowAuditLogs(false)} />
            </Modal>
          </Space>
        }
        style={{
          height: '100%',
          paddingBottom: 24
        }}
      />
    </>
  )
}

export default UserManagement;